'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
const API_BASE = process.env.REACT_APP_REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
export default function CandidateAssessment() {
    const { recordId, kind } = useParams();
    const [searchParams] = useSearchParams();
    const sig = searchParams.get('sig') || '';
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [excelAnswers, setExcelAnswers] = useState({});
    const [discAnswers, setDiscAnswers] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const isValidKind = kind === 'excel' || kind === 'disc';
    useEffect(() => {
        if (!recordId || !isValidKind || !sig) {
            setError('This link is invalid.');
            setLoading(false);
            return;
        }
        fetch(`${API_BASE}/applicant-records/${recordId}/assessments/${kind}/context?sig=${encodeURIComponent(sig)}`)
            .then((r) => r.json())
            .then((res) => {
            if (!res.success) {
                setError(res.message || 'This link could not be verified.');
                setLoading(false);
                return;
            }
            setContext(res.data);
            if (kind === 'excel') {
                const init = {};
                res.data.questions.forEach((q) => { init[q.qNo] = ''; });
                setExcelAnswers(init);
            }
            else {
                setDiscAnswers(new Array(res.data.questions.length).fill(''));
            }
            setLoading(false);
        })
            .catch(() => { setError('Something went wrong loading this assessment.'); setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordId, kind, sig]);
    const handleSubmit = async () => {
        if (kind === 'excel') {
            const questions = context.questions;
            if (questions.some((q) => !excelAnswers[q.qNo]?.trim())) {
                setError('Please answer every question before submitting.');
                return;
            }
        }
        else {
            if (discAnswers.some((a) => !a)) {
                setError('Please answer every question before submitting.');
                return;
            }
        }
        setSubmitting(true);
        setError('');
        try {
            const body = kind === 'excel'
                ? {
                    answers: context.questions.map((q) => (q.type === 'mcq'
                        ? { qNo: q.qNo, selectedOption: excelAnswers[q.qNo] }
                        : { qNo: q.qNo, answer: excelAnswers[q.qNo] })),
                }
                : { answers: discAnswers };
            const res = await fetch(`${API_BASE}/applicant-records/${recordId}/assessments/${kind}/submit?sig=${encodeURIComponent(sig)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setError(data.message || 'Failed to submit.');
                setSubmitting(false);
                return;
            }
            setSubmitted(true);
        }
        catch {
            setError('Failed to submit.');
        }
        finally {
            setSubmitting(false);
        }
    };
    if (loading) {
        return _jsx("div", { className: "min-h-screen flex items-center justify-center text-gray-500", children: "Loading\u2026" });
    }
    if (!isValidKind || (error && !context)) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gray-50 text-center px-4", children: _jsx("p", { className: "text-gray-600", children: error || 'This link is invalid.' }) }));
    }
    if (submitted || (context && (kind === 'excel' ? context.status === 'Submitted' || context.status === 'Graded' : context.status === 'Completed'))) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-lime-50 text-center px-4", children: _jsxs("div", { children: [_jsx("h2", { className: "text-2xl font-bold text-lime-800 mb-2", children: "Thank you!" }), _jsxs("p", { className: "text-gray-700", children: ["Your ", kind === 'excel' ? 'Excel Test' : 'DISC Assessment', " has been submitted."] })] }) }));
    }
    const title = kind === 'excel' ? 'Basic Excel Skills Test' : 'DISC Assessment';
    return (_jsxs("div", { className: "min-h-screen bg-gradient-to-b from-lime-50 to-white", children: [_jsx("nav", { className: "bg-lime-600 shadow-xl", children: _jsxs("div", { className: "container mx-auto px-6 py-6", children: [_jsx("h1", { className: "text-white text-2xl sm:text-3xl font-bold", children: title }), context?.full_name && _jsxs("p", { className: "text-lime-100 text-sm mt-1", children: ["Hi ", context.full_name, ", please complete the assessment below."] }), kind === 'excel' && (_jsx("p", { className: "text-lime-100 text-xs mt-2", children: "20 multiple-choice questions, then 10 practical tasks \u2014 write the actual formula or describe the steps, don't just name the feature." }))] }) }), _jsx("div", { className: "container mx-auto px-6 py-10 max-w-2xl", children: _jsxs("div", { className: "bg-white rounded-xl shadow p-6 space-y-6", children: [kind === 'excel'
                            ? context.questions.map((q) => (_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-semibold text-gray-800 mb-1", children: [q.qNo, ". ", q.question] }), q.sampleData && (_jsxs("p", { className: "text-xs text-gray-500 mb-2", children: ["Sample data: ", q.sampleData] })), q.type === 'mcq' && q.options ? (_jsx("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2", children: Object.keys(q.options).map((opt) => (_jsxs("button", { type: "button", onClick: () => setExcelAnswers((prev) => ({ ...prev, [q.qNo]: opt })), className: `border rounded-lg px-3 py-2 text-sm text-left transition ${excelAnswers[q.qNo] === opt ? 'border-lime-500 bg-lime-50 text-lime-800 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`, children: [_jsxs("span", { className: "font-bold mr-1", children: [opt, "."] }), q.options[opt]] }, opt))) })) : (_jsx("textarea", { value: excelAnswers[q.qNo] || '', onChange: (e) => setExcelAnswers((prev) => ({ ...prev, [q.qNo]: e.target.value })), rows: 3, className: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono mt-2 focus:outline-none focus:ring-2 focus:ring-lime-400", placeholder: "Write the actual formula, or describe the steps..." }))] }, q.qNo)))
                            : context.questions.map((q, i) => (_jsxs("div", { children: [_jsxs("p", { className: "text-sm font-semibold text-gray-800 mb-2", children: [i + 1, ". Which word best describes you?"] }), _jsx("div", { className: "grid grid-cols-2 gap-2", children: Object.keys(q).map((trait) => (_jsx("button", { type: "button", onClick: () => setDiscAnswers((prev) => prev.map((a, idx) => idx === i ? trait : a)), className: `border rounded-lg px-3 py-2 text-sm text-left transition ${discAnswers[i] === trait ? 'border-lime-500 bg-lime-50 text-lime-800 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`, children: q[trait] }, trait))) })] }, i))), error && _jsx("p", { className: "text-red-600 text-sm", children: error }), _jsx("button", { onClick: handleSubmit, disabled: submitting, className: "w-full bg-lime-600 hover:bg-lime-700 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition", children: submitting ? 'Submitting…' : 'Submit' })] }) })] }));
}
